import { __awaiter, __decorate } from "tslib";
import { AuthError } from '@genesislcap/foundation-comms';
import { inIFrame, inSymphonyDesktop, openPopup } from '@genesislcap/foundation-utils';
import { customElement, DOM, observable, volatile } from '@microsoft/fast-element';
import { Route } from '@microsoft/fast-router';
import { logger, getSSOLoginURL, fetchIDPs } from '../../utils';
import { BaseRoute } from '../base';
import { LoginFormStyles as styles } from './login-form.styles';
import { LoginFormTemplate as template } from './login-form.template';
let LoginForm = class LoginForm extends BaseRoute {
    constructor() {
        super(...arguments);
        this.idps = [];
        this.selectedIDP = '';
        this.ssoToggled = false;
        this.toggleHttpMode = (e) => {
            if (!this.connect)
                return;
            // TODO: ability to swap between Connect and HttpConnect on the fly
            this.convertHostUrl();
        };
    }
    connectedCallback() {
        const _super = Object.create(null, {
            connectedCallback: { get: () => super.connectedCallback }
        });
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            _super.connectedCallback.call(this);
            this.ssoToggled = (_b = (_a = this.config.sso) === null || _a === void 0 ? void 0 : _a.toggled) !== null && _b !== void 0 ? _b : false;
            if (this.connect.isConnected && this.auth.isLoggedIn) {
                yield this.credentialManager.preventSilentAccess();
                yield this.auth.logout();
            }
            this.convertHostUrl();
            const shouldAutofill = !this.ssoToggled &&
                !this.credentialManager.validateRefreshCredentials(this.credentialManager.createRefreshCredentials());
            if (shouldAutofill) {
                yield this.autofill();
            }
        });
    }
    enter() {
        return __awaiter(this, void 0, void 0, function* () {
            const queryParams = new URLSearchParams(window.location.search);
            const mfaToken = queryParams.get('MFA_CODE');
            if (mfaToken) {
                this.session.setSessionStorageItem('mfaToken', mfaToken);
            }
            DOM.queueUpdate(() => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (!this.connect.isConnected && this.config.autoConnect) {
                        yield this.toggleConnection();
                    }
                    if (!this.auth.isLoggedIn && this.config.autoAuth) {
                        this.attemptLoginWhenConnected();
                    }
                }
                catch (e) {
                    logger.debug(e);
                }
            }));
        });
    }
    toggleConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connect.isConnected) {
                this.connect.disconnect();
                logger.debug(`Disconnected from ${this.host}`);
                return;
            }
            return this.connect.connect(this.host);
        });
    }
    attemptLoginWhenConnected() {
        this.connect.isConnectedSubject.subscribe((isConnected) => {
            if (isConnected) {
                /**
                 * Login preference flow taken from @genesislcap/foundation-login.
                 * We should sit down with Jose and team to cross check this. Reference code is bit hard to follow in places.
                 */
                const cm = this.credentialManager;
                const refreshCredentials = cm.createRefreshCredentials();
                if (cm.validateRefreshCredentials(refreshCredentials)) {
                    logger.debug(`Attempting to login with refresh credentials.`);
                    this.login(refreshCredentials);
                    return;
                }
                if (this.config.sso) {
                    const ssoCredentials = cm.createSSOCredentials();
                    if (cm.validateSSOCredentials(ssoCredentials)) {
                        logger.debug(`Attempting to login with SSO.`);
                        this.login(ssoCredentials);
                        this.credentialManager.removeSSOToken(); // < sso tokens can only be used once, remove after login attempt
                        return;
                    }
                }
                this.attemptBasicLogin();
            }
        });
    }
    attemptBasicLogin() {
        const basicCredentials = this.credentialManager.createBasicCredentials({
            username: this.orgUsername,
            password: this.password,
        });
        if (!this.credentialManager.validateBasicCredentials(basicCredentials)) {
            logger.debug(`Basic credentials appear invalid, aborting login attempt.`);
            return;
        }
        logger.debug(`Attempting to login with basic credentials.`);
        this.login(basicCredentials);
    }
    ssoToggledChanged() {
        var _a, _b;
        this.clearError();
        if (this.ssoToggled) {
            const path = `${(_b = (_a = this.config.sso) === null || _a === void 0 ? void 0 : _a.identityProvidersPath) !== null && _b !== void 0 ? _b : 'sso/list'}`;
            fetchIDPs(path)
                .then((idps) => {
                var _a, _b, _c;
                logger.debug('fetched IDPs', idps);
                // it no longer throws, check for 'error' manually
                if (!idps.length || (idps.length === 1 && ((_a = idps[0]) === null || _a === void 0 ? void 0 : _a.id) === 'error')) {
                    this.setError('Failed to fetch identity providers.');
                }
                else {
                    this.idps = idps;
                    this.selectedIDP = (_c = (_b = this.idps) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id;
                }
            })
                .catch(() => this.setError('Failed to fetch identity providers.'));
        }
    }
    get selectedIDPName() {
        return this.selectedIDP ? ` - ${this.selectedIDP}` : '';
    }
    onClickSSO() {
        const selectedIDP = this.idps.find((idp) => idp.id === this.selectedIDP);
        const ssoLoginUrl = getSSOLoginURL(selectedIDP);
        if (inIFrame()) {
            if (inSymphonyDesktop()) {
                // symphony/electron opens new window in the default browser instead
                // popup within electron, so we have to navigate manually
                openPopup('', 'symphony').location.assign(ssoLoginUrl);
            }
            else {
                openPopup(ssoLoginUrl, 'Login');
            }
        }
        else {
            window.open(ssoLoginUrl, '_self');
        }
    }
    convertHostUrl() {
        var _a, _b, _c, _d;
        if (((_a = this.connect) === null || _a === void 0 ? void 0 : _a.httpMode()) && ((_b = this.host) === null || _b === void 0 ? void 0 : _b.startsWith('ws'))) {
            this.host = this.host.replace('ws', 'http');
        }
        else if (!((_c = this.connect) === null || _c === void 0 ? void 0 : _c.httpMode()) && ((_d = this.host) === null || _d === void 0 ? void 0 : _d.startsWith('http'))) {
            this.host = this.host.replace('http', 'ws');
        }
    }
    onHostSubmit() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.toggleConnection();
        });
    }
    onLoginSubmit() {
        const _super = Object.create(null, {
            onSubmit: { get: () => super.onSubmit }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.onSubmit.call(this);
            DOM.queueUpdate(() => {
                this.attemptBasicLogin();
            });
        });
    }
    login(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { authToken } = yield this.auth.login(credentials);
                if (!authToken) {
                    throw new Error('Unable to login. Try again later.');
                }
                this.storeCredentials();
                let url = this.config.defaultRedirectUrl;
                if (this.session.returnUrl &&
                    !this.config.omitRedirectUrls.includes(this.session.returnUrl)) {
                    url = this.session.returnUrl;
                }
                Route.path.push(url);
            }
            catch (err) {
                if (err instanceof AuthError) {
                    if (err.hasPasswordExpiredError()) {
                        this.storeCredentials();
                        this.onNavigate(this.getSubRoutePath('reset-password?expired'));
                        return;
                    }
                    if (err.hasMFAError()) {
                        this.storeCredentials();
                        this.session.setSessionStorageItem('refreshToken', err.received.REFRESH_AUTH_TOKEN);
                        this.session.setSessionStorageItem('username', this.username);
                        this.setError('Please check your inbox for your login link.');
                        return;
                    }
                    if (this.config.fields.organisation &&
                        this.config.fields.organisation.required !== false &&
                        !this.organisation.length) {
                        this.setError(err.message + 'Please fill in Comp Id.');
                        return;
                    }
                    this.setError(err.message + err.getMessageError());
                    return;
                }
                this.setError(err.message);
            }
        });
    }
    get connectActionText() {
        return this.connect.isConnected ? 'Disconnect' : 'Connect';
    }
    get submitConnectDisabled() {
        return !this.host;
    }
    get submitDisabled() {
        return !this.connect.httpMode() && (this.connect.isWorking || !this.connect.isConnected);
    }
};
__decorate([
    observable
], LoginForm.prototype, "idps", void 0);
__decorate([
    observable
], LoginForm.prototype, "selectedIDP", void 0);
__decorate([
    observable
], LoginForm.prototype, "ssoToggled", void 0);
__decorate([
    volatile
], LoginForm.prototype, "selectedIDPName", null);
__decorate([
    volatile
], LoginForm.prototype, "connectActionText", null);
LoginForm = __decorate([
    customElement({
        name: 'foundation-login-form',
        template,
        styles,
    })
], LoginForm);
export { LoginForm };
